import React, { useContext } from "react";
import { Typography, Input, Box } from "@mui/material";
import InputMask from "react-input-mask";
import { ErrorMessage } from "formik";
import FormError from "../../FormError";
import { viewportContext } from "../../../context/ViewportContext";
import { tokens } from "../../../theme/tokens";

interface MaskFieldProps {
  label: string;
  value: string;
  setFieldValue: (field: string, value: any) => void;
  name: string;
  mask: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const MaskField = ({
  label,
  value,
  setFieldValue,
  name,
  mask,
  required = false,
  placeholder = "",
  disabled = false,
}: MaskFieldProps) => {
  const { tallScreen } = useContext(viewportContext) as {
    tallScreen?: boolean;
  };

  const parentStyle = {
    flex: 1,
    width: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
  };

  const rowStyle = {
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.space[1],
  };
  const labelStyle = {
    display: "flex",
    width: "100%",
    alignItems: "center",
    "& p": {
      fontSize: tokens.type.caption.size,
      fontWeight: tokens.type.caption.weight,
      lineHeight: tokens.type.caption.lineHeight,
      color: tokens.color.text.secondary,
    },
  };
  const fieldStyle = { width: "100%" };
  const inputStyle = {
    width: "100%",
    border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: tokens.radius.sm,
    height: tokens.density.field,
    paddingLeft: "3px",
    backgroundColor: tokens.color.surface.default,
    transition: `border-color ${tokens.motion.fast} ${tokens.motion.easing}`,
    "&:hover": { borderColor: tokens.color.border.strong },
    "&.Mui-focused": { borderColor: tokens.color.focus.ring },
    "& .MuiInputBase-input": {
      textIndent: "10px",
      textTransform: "capitalize" as const,
      color: tokens.color.text.primary,
      fontSize: tokens.type.dense.size,
    },
  };

  return (
    <Box sx={tallScreen ? parentStyle : parentStyle}>
      <Box sx={rowStyle}>
        <Box sx={labelStyle}>
          <Typography>
            {label}
            {required && (
              <span
                style={{ color: tokens.color.status.error, fontWeight: 500 }}
              >
                *
              </span>
            )}
          </Typography>
        </Box>
        <Box sx={fieldStyle}>
          <InputMask
            mask={mask}
            value={value}
            disabled={disabled}
            onChange={(e: { target: { value: any } }) =>
              setFieldValue(name, e.target.value)
            }
          >
            {(inputProps: any) => (
              <Input
                {...inputProps}
                type="text"
                placeholder={placeholder}
                disableUnderline
                sx={inputStyle}
              />
            )}
          </InputMask>
        </Box>
      </Box>
      <ErrorMessage
        name={name}
        component={(props: any) => (
          <Box
            sx={{
              fontSize: tokens.type.caption.size,
              color: tokens.color.status.error,
              mt: tokens.space[1],
            }}
          >
            <FormError {...props} />
          </Box>
        )}
      />
    </Box>
  );
};

export default MaskField;
